import { useState, useEffect } from "react";
import axiosService from "../core/services/axios-service";
import { ApiRoutes } from "../core/dictionary/api-url";
import ToastService from "../core/services/toaster-service";
import Loader from "./designs/loader";

function toISO(date, time) {
  if (!date || !time) return null;
  return new Date(`${date}T${time}:00`).toISOString();
}

// "day1" → "Day 1", "afternoon" → "Afternoon"
function formatLabel(val) {
  return val
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isoToDateField(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toISOString().slice(0, 10);
}

function isoToTimeField(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toISOString().slice(11, 16);
}

function formatDateTime(isoStr) {
  if (!isoStr) return "—";
  return new Date(isoStr).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const emptyOnline = { startDate: "", startTime: "", endDate: "", endTime: "" };
const emptyOffline = { day: "", session: "", location: "", date: "", time: "", endDate: "", endTime: "" };

export default function ManageSchedule({ organizationId, isOpen, onSuccess }) {
  const [scheduleData, setScheduleData] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [trainingType, setTrainingType] = useState("");
  const [online, setOnline] = useState(emptyOnline);
  const [offline, setOffline] = useState(emptyOffline);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [openPanel, setOpenPanel] = useState(null);
  const [selectedOfflineSlot, setSelectedOfflineSlot] = useState(null);

  const togglePanel = (panel) =>
    setOpenPanel((prev) => (prev === panel ? null : panel));

  // Fetch schedule data each time the drawer is opened
  useEffect(() => {
    if (!isOpen || !organizationId) return;

    setFetchLoading(true);
    setScheduleData([]);
    setTrainingType("");
    setOnline(emptyOnline);
    setOffline(emptyOffline);
    setSelectedOfflineSlot(null);
    setError("");

    axiosService
      .get(`${ApiRoutes.TRAININGS_BY_ORGANIZATION}/${organizationId}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const data = res.data.data;
          setScheduleData(data);
          // Auto-select first offline slot by default
          const offlineRecord = data.find((r) => r.type === "offline");
          if (offlineRecord?.offlineSlots?.length > 0) {
            setSelectedOfflineSlot(offlineRecord.offlineSlots[0]);
          }
        }
      })
      .catch(() => ToastService.error("Failed to load schedule data."))
      .finally(() => setFetchLoading(false));
  }, [isOpen, organizationId]);

  // Build a lookup map: { online: <record>, offline: <record> }
  const scheduleMap = scheduleData.reduce((acc, item) => {
    acc[item.type] = item;
    return acc;
  }, {});

  // Training type options derived from API data
  const trainingTypes = scheduleData.map((item) => item.type);

  // Offline slot options derived from API offlineSlots
  const allSlots = scheduleMap.offline?.offlineSlots ?? [];
  const availableDays = [...new Set(allSlots.map((s) => s.day))];
  const availableSessions = offline.day
    ? [...new Set(allSlots.filter((s) => s.day === offline.day).map((s) => s.session))]
    : [];

  const handleTrainingTypeChange = (type) => {
    setTrainingType(type);
    setError("");

    if (type === "online") {
      const existing = scheduleMap.online;
      setOnline(
        existing?.status === "scheduled"
          ? {
              startDate: isoToDateField(existing.startDate),
              startTime: isoToTimeField(existing.startDate),
              endDate: isoToDateField(existing.endDate),
              endTime: isoToTimeField(existing.endDate),
            }
          : emptyOnline
      );
      setOffline(emptyOffline);
    } else {
      setOffline(emptyOffline);
      setOnline(emptyOnline);
    }
  };

  const handleOnlineChange = (e) =>
    setOnline((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleOfflineChange = (e) => {
    const { name, value } = e.target;
    const resolvedDay = name === "day" ? value : offline.day;
    const resolvedSession = name === "session" ? value : offline.session;
    const bothSelected = resolvedDay && resolvedSession;
    const existing = scheduleMap.offline;

    setOffline((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "day" ? { session: "" } : {}),
      ...(bothSelected && existing
        ? {
            location: existing.location || "",
            date: isoToDateField(existing.startDate),
            time: isoToTimeField(existing.startDate),
            endDate: isoToDateField(existing.endDate),
            endTime: isoToTimeField(existing.endDate),
          }
        : {}),
    }));
  };

  const validate = () => {
    if (!trainingType) return "Please select a Training Type.";
    if (trainingType === "online") {
      if (!online.startDate || !online.startTime) return "Please enter Start Date and Time.";
      if (!online.endDate || !online.endTime) return "Please enter End Date and Time.";
      const start = new Date(`${online.startDate}T${online.startTime}`);
      const end = new Date(`${online.endDate}T${online.endTime}`);
      if (end <= start) return "End Date/Time must be after Start Date/Time.";
    } else {
      if (!offline.day) return "Please select a Day.";
      if (!offline.session) return "Please select a Session.";
      if (!offline.location.trim()) return "Please enter a Location.";
      if (!offline.date) return "Please enter a Start Date.";
      if (!offline.time) return "Please enter a Start Time.";
      if (!offline.endDate) return "Please enter an End Date.";
      if (!offline.endTime) return "Please enter an End Time.";
      const offStart = new Date(`${offline.date}T${offline.time}`);
      const offEnd = new Date(`${offline.endDate}T${offline.endTime}`);
      if (offEnd <= offStart) return "End Date/Time must be after Start Date/Time.";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setSubmitLoading(true);
    try {
      let payload;
      if (trainingType === "online") {
        payload = {
          type: "online",
          trainingId: scheduleMap.online?._id,
          startDate: toISO(online.startDate, online.startTime),
          endDate: toISO(online.endDate, online.endTime),
        };
      } else {
        const matchingSlot = allSlots.find(
          (s) => s.day === offline.day && s.session === offline.session
        );
        payload = {
          type: "offline",
          trainingId: scheduleMap.offline?._id,
          slotId: matchingSlot?._id,
          location: offline.location,
          startDate: toISO(offline.date, offline.time),
          endDate: toISO(offline.endDate, offline.endTime),
        };
      }
      await axiosService.put(
        `${ApiRoutes.SCHEDULE_TRAINING}/${organizationId}`,
        payload
      );
      ToastService.success("Schedule saved successfully.");
      if (onSuccess) onSuccess();
    } catch {
      setError("Server error. Please try again.");
      ToastService.error("Server error. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="manage-schedule-loader">
        <Loader text="Loading schedule..." />
      </div>
    );
  }

  return (
    <><form onSubmit={handleSubmit} className="p-3 manage-schedule-form">

      {/* Training Type — options from API */}
      <div className="mb-4">
        <label className="form-label fw-semibold">Training Type</label>
        <div className="d-flex gap-3">
          {trainingTypes.map((type) => (
            <div
              key={type}
              className={`form-check${trainingType === type ? " form-check--selected" : ""}`}
            >
              <input
                className="form-check-input"
                type="radio"
                name="trainingType"
                id={`tt-${type}`}
                value={type}
                checked={trainingType === type}
                onChange={() => handleTrainingTypeChange(type)}
              />
              <label className="form-check-label" htmlFor={`tt-${type}`}>
                {formatLabel(type)}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Online Training Fields */}
      {trainingType === "online" && (
        <>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                name="startDate"
                className="form-control"
                value={online.startDate}
                onChange={handleOnlineChange}
              />
            </div>
            <div className="col-6">
              <label className="form-label">Start Time</label>
              <input
                type="time"
                name="startTime"
                className="form-control"
                value={online.startTime}
                onChange={handleOnlineChange}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label">End Date</label>
              <input
                type="date"
                name="endDate"
                className="form-control"
                value={online.endDate}
                onChange={handleOnlineChange}
              />
            </div>
            <div className="col-6">
              <label className="form-label">End Time</label>
              <input
                type="time"
                name="endTime"
                className="form-control"
                value={online.endTime}
                onChange={handleOnlineChange}
              />
            </div>
          </div>
        </>
      )}

      {/* Offline Training Fields */}
      {trainingType === "offline" && (
        <>
          {/* Day — options from API offlineSlots */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Select Day</label>
            <div className="d-flex gap-3">
              {availableDays.map((day) => (
                <div
                  key={day}
                  className={`form-check${offline.day === day ? " form-check--selected" : ""}`}
                >
                  <input
                    className="form-check-input"
                    type="radio"
                    name="day"
                    id={`day-${day}`}
                    value={day}
                    checked={offline.day === day}
                    onChange={handleOfflineChange}
                  />
                  <label className="form-check-label" htmlFor={`day-${day}`}>
                    {formatLabel(day)}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Session — options filtered by selected day */}
          {offline.day && (
            <div className="mb-3">
              <label className="form-label fw-semibold">Select Session</label>
              <div className="d-flex gap-3">
                {availableSessions.map((session) => (
                  <div
                    key={session}
                    className={`form-check${offline.session === session ? " form-check--selected" : ""}`}
                  >
                    <input
                      className="form-check-input"
                      type="radio"
                      name="session"
                      id={`session-${session}`}
                      value={session}
                      checked={offline.session === session}
                      onChange={handleOfflineChange}
                    />
                    <label className="form-check-label" htmlFor={`session-${session}`}>
                      {formatLabel(session)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Location, Date, Time */}
          {offline.day && offline.session && (
            <>
              <div className="mb-3">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  name="location"
                  className="form-control"
                  placeholder="Enter training location"
                  value={offline.location}
                  onChange={handleOfflineChange}
                />
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    name="date"
                    className="form-control"
                    value={offline.date}
                    onChange={handleOfflineChange}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    name="time"
                    className="form-control"
                    value={offline.time}
                    onChange={handleOfflineChange}
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    className="form-control"
                    value={offline.endDate}
                    onChange={handleOfflineChange}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    name="endTime"
                    className="form-control"
                    value={offline.endTime}
                    onChange={handleOfflineChange}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {error && <div className="text-danger mb-3">{error}</div>}

      {trainingType && (
        <button
          type="submit"
          className="btn btn-gradient w-100"
          disabled={submitLoading}
        >
          {submitLoading ? "Saving..." : "Save Schedule"}
        </button>
      )}
    </form>


    <div className="schedule-info-accordion p-3">
      <p className="schedule-info-accordion__heading">Schedule Information</p>

      {/* Online Panel */}
      {["online", "offline"].map((type) => {
        const record = scheduleMap[type];
        const isScheduled = record?.status === "scheduled";
        const isOpen = openPanel === type;

        return (
          <div key={type} className="schedule-info-panel">
            <button
              type="button"
              className={`schedule-info-panel__header${isOpen ? " schedule-info-panel__header--open" : ""}`}
              onClick={() => togglePanel(type)}
            >
              <span className="schedule-info-panel__title">
                {formatLabel(type)} Training
              </span>
              <span className={`schedule-info-panel__badge${isScheduled ? " schedule-info-panel__badge--scheduled" : " schedule-info-panel__badge--not-scheduled"}`}>
                {isScheduled ? "Scheduled" : "Not Scheduled"}
              </span>
              <span className="schedule-info-panel__chevron">{isOpen ? "▲" : "▼"}</span>
            </button>

            {isOpen && (
              <div className="schedule-info-panel__body">
                {!isScheduled ? (
                  <p className="schedule-info-panel__empty">No schedule has been set for this training type.</p>
                ) : type === "online" ? (
                  <div className="schedule-info-grid">
                    <div className="schedule-info-grid__item">
                      <span className="schedule-info-grid__label">Start</span>
                      <span className="schedule-info-grid__value">{formatDateTime(record.startDate)}</span>
                    </div>
                    <div className="schedule-info-grid__item">
                      <span className="schedule-info-grid__label">End</span>
                      <span className="schedule-info-grid__value">{formatDateTime(record.endDate)}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {record.offlineSlots?.length > 0 && (
                      <>
                        <p className="schedule-info-grid__label mb-2">Slots</p>
                        <div className="schedule-info-slots mb-3">
                          {record.offlineSlots.map((slot) => {
                            const isActive = selectedOfflineSlot?._id === slot._id;
                            return (
                              <button
                                key={slot._id}
                                type="button"
                                className={`schedule-info-slot${isActive ? " schedule-info-slot--active" : ""}`}
                                onClick={() => setSelectedOfflineSlot(slot)}
                              >
                                {formatLabel(slot.day)} · {formatLabel(slot.session)}
                              </button>
                            );
                          })}
                        </div>
                        {selectedOfflineSlot && (
                          <div className="schedule-info-grid">
                            <div className="schedule-info-grid__item">
                              <span className="schedule-info-grid__label">Day</span>
                              <span className="schedule-info-grid__value">{formatLabel(selectedOfflineSlot.day)}</span>
                            </div>
                            <div className="schedule-info-grid__item">
                              <span className="schedule-info-grid__label">Session</span>
                              <span className="schedule-info-grid__value">{formatLabel(selectedOfflineSlot.session)}</span>
                            </div>
                            <div className="schedule-info-grid__item">
                              <span className="schedule-info-grid__label">Location</span>
                              <span className="schedule-info-grid__value">{record.location || "—"}</span>
                            </div>
                            <div className="schedule-info-grid__item">
                              <span className="schedule-info-grid__label">Start</span>
                              <span className="schedule-info-grid__value">{formatDateTime(record.startDate)}</span>
                            </div>
                            <div className="schedule-info-grid__item">
                              <span className="schedule-info-grid__label">End</span>
                              <span className="schedule-info-grid__value">{formatDateTime(record.endDate)}</span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </>
  );
}
